import React from 'react';
import { Box, Card, Grid, Skeleton, Typography } from '@mui/material';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { formatMoney } from '../../utils/configUtils';

const CARD_CONFIGS = [
  {
    key: 'totalUsers',
    growthKey: 'usersGrowthPercentage',
    label: 'Total Users',
    icon: PeopleAltOutlinedIcon,
    color: '#065F46',
    format: (v) => (v ?? 0).toLocaleString(),
  },
  {
    key: 'totalVendors',
    growthKey: 'vendorsGrowthPercentage',
    label: 'Total Vendors',
    icon: StorefrontOutlinedIcon,
    color: '#0D7B5C',
    format: (v) => (v ?? 0).toLocaleString(),
  },
  {
    key: 'totalOrders',
    growthKey: 'ordersGrowthPercentage',
    label: 'Total Orders',
    icon: ShoppingCartOutlinedIcon,
    color: '#047857',
    format: (v) => (v ?? 0).toLocaleString(),
  },
  {
    key: 'totalProducts',
    growthKey: 'productsGrowthPercentage',
    label: 'Total Products',
    icon: InventoryOutlinedIcon,
    color: '#059669',
    format: (v) => (v ?? 0).toLocaleString(),
  },
  {
    key: 'totalEarnings',
    growthKey: 'earningsGrowthPercentage',
    label: 'Gross Revenue',
    sublabel: 'Total buyer payments (all-time)',
    icon: AttachMoneyIcon,
    color: '#0D9488',
    format: formatMoney,
  },
  {
    key: 'totalPlatformFee',
    growthKey: 'platformFeeGrowthPercentage',
    label: 'Platform Commission',
    sublabel: 'App fee collected (all-time)',
    icon: AccountBalanceOutlinedIcon,
    color: '#362B24',
    format: formatMoney,
  },
];

const GrowthBadge = ({ pct }) => {
  const num = pct ?? 0;
  if (num === 0) {
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1.5, py: 0.4, borderRadius: '12px', bgcolor: '#f3f4f6', fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
        —
      </Box>
    );
  }
  const pos = num > 0;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, px: 1.5, py: 0.4, borderRadius: '12px', bgcolor: pos ? '#dcfce7' : '#fee2e2', fontSize: 12, fontWeight: 600, color: pos ? '#166534' : '#991b1b' }}>
      {pos
        ? <ArrowUpwardIcon sx={{ fontSize: 13 }} />
        : <ArrowDownwardIcon sx={{ fontSize: 13 }} />}
      {pos ? '+' : ''}{num.toFixed(2)}%
    </Box>
  );
};

const KpiCardSkeleton = () => (
  <Card sx={{ borderRadius: '24px', boxShadow: '3px 2px 20px 0px rgba(0,0,0,0.04)', p: 3, minHeight: 210, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
    <Skeleton variant="circular" width={52} height={52} />
    <Skeleton variant="text" width="55%" height={38} />
    <Skeleton variant="text" width="70%" height={20} />
    <Skeleton variant="rectangular" width={80} height={26} sx={{ borderRadius: '12px' }} />
  </Card>
);

const KpiCard = ({ config, data }) => {
  const IconComp = config.icon;
  const rawValue = data?.[config.key] ?? 0;
  const pct = data?.[config.growthKey] ?? 0;

  return (
    <Card sx={{ borderRadius: '24px', boxShadow: '3px 2px 20px 0px rgba(0,0,0,0.04)', p: 3, minHeight: 210, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 0.75 }}>
      <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: `${config.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.5 }}>
        <IconComp sx={{ color: config.color, fontSize: 26 }} />
      </Box>
      <Typography sx={{ fontSize: '26px', fontWeight: 700, color: '#000', fontFamily: 'Heebo', wordBreak: 'break-word', lineHeight: 1.2 }}>
        {config.format(rawValue)}
      </Typography>
      <Typography sx={{ fontSize: 14, color: '#362B24', opacity: 0.6, fontFamily: 'Heebo' }}>
        {config.label}
      </Typography>
      {config.sublabel && (
        <Typography sx={{ fontSize: 11, color: '#362B24', opacity: 0.4, fontFamily: 'Heebo' }}>
          {config.sublabel}
        </Typography>
      )}
      <GrowthBadge pct={pct} />
    </Card>
  );
};

const KpiGrid = ({ data, loading, keys }) => {
  const configs = keys ? CARD_CONFIGS.filter((c) => keys.includes(c.key)) : CARD_CONFIGS;
  return (
    <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
      {configs.map((config) => (
        <Grid item xs={12} sm={6} md={4} key={config.key}>
          {loading ? <KpiCardSkeleton /> : <KpiCard config={config} data={data} />}
        </Grid>
      ))}
    </Grid>
  );
};

export default KpiGrid;
